/**
 * useKeyboardAware — keyboard-avoidance for native iOS / web
 *
 * On focus of an input/textarea anywhere in the page, scroll the focused
 * element to the centre of the visible viewport AFTER the keyboard finishes
 * animating in (~250ms on iOS WKWebView). This prevents the "input hidden
 * behind keyboard" / "Continue button cut off" problem on onboarding screens.
 *
 * Capacitor's @capacitor/keyboard plugin is already configured with
 * `resize: 'body'` in capacitor.config.ts — that shrinks the visual viewport
 * when the keyboard opens. Combined with this hook's scrollIntoView, the
 * focused input + the Continue button below it stay visible without any
 * per-screen layout gymnastics.
 *
 * Use it once at the top of any onboarding page that has text inputs:
 *   useKeyboardAware();
 */
import { useEffect } from 'react';

export default function useKeyboardAware({ delayMs = 280, block = 'center' } = {}) {
  useEffect(() => {
    const handleFocusIn = (event) => {
      const target = event.target;
      if (!target || !(target.matches && target.matches('input, textarea, [contenteditable="true"]'))) {
        return;
      }
      // Wait for the keyboard's slide-in animation so scroll math is accurate
      // against the post-resize viewport.
      window.setTimeout(() => {
        try {
          target.scrollIntoView({ block, behavior: 'smooth' });
        } catch {
          // Older Safari fallback
          target.scrollIntoView();
        }
      }, delayMs);
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [delayMs, block]);
}
